import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment';
import { createDoorPostprocessing, loadAntialiasing } from '../geometry/doorPostprocessing';
import { DEFAULT_AO_SETTINGS } from '../geometry/ambientOcclusion';
import { readEnvironmentBlob } from '../geometry/environmentLibrary';
import { readPublicEnvironmentBlob } from '../services/studioAppearance';
import PreviewFailure from './PreviewFailure';
import { leafOpeningsForLayout } from '../geometry/doorLeafGeometry';
import { resolveWindowLayout } from '../geometry/windowLayout';
import { assemblyClearances } from '../geometry/assemblyClearance';
import { doorOpeningAxis, doorLockPlacement, orientLeafDesignForExterior } from '../geometry/doorOpening';
import { createRenderCamera, DEFAULT_RENDER, renderPixelSize, validateRenderSettings } from '../geometry/renderCapture';
import { loadDoorLeafBuilder } from '../geometry/loadDoorLeafBuilder';
import { CASSETTE, createWindowCassetteGeometries } from '../geometry/windowCassetteGeometry';
import { doorFamilyDefaults, familyThreshold } from '../geometry/doorFamily';
import { leafDimensions } from '../geometry/leafDimensions';
import { resolveWaternose } from '../geometry/waternoseGeometry';
import { resolveDoorHardware } from '../geometry/doorManufacturing';
import { timberLeafSurface, usesPineTrim } from '../geometry/timberLeafSurface';
import { isClearLeafGlass } from '../data/leafGlass';

// CRA injects process.env at build time; tsconfig has no node types.
declare const process: { env: Record<string, string | undefined> };
declare const module: { hot?: { dispose: (cb: () => void) => void } };

// Dev only: when this file is hot-swapped, do a full reload so the Three.js
// scene and GLB always rebuild cleanly. Each page load starts with a fresh camera.
if (process.env.NODE_ENV === 'development' && typeof module !== 'undefined' && module.hot) {
  module.hot.dispose(() => window.location.reload());
}

// Three.js GLB door previewer.
// Expects public/models/DoorDynamic.glb with the following frame parts:
//   - 60mm_bottomleft_Oak  (bottom-left corner)
//   - 60mm_topleft         (top-left corner, already Y-flipped in the GLB)
//   - 60mm_Left            (left vertical frame bar, stretched to door height)
//   - 60mm_top             (top horizontal frame bar, stretched to door width)
// The left/top bars are mirrored to create the right/bottom bars.

interface LeafAnchor { edge: 'start' | 'end' | 'center' | 'ratio' | 'stretch'; offsetMm: number; ratio?: number; endOffsetMm?: number }
interface Props {
  sceneKey?: string;
  renderSettings?: typeof DEFAULT_RENDER;
  viewportAO?: boolean;
  freezeAutoFit?: boolean;
  aoSettings?: typeof DEFAULT_AO_SETTINGS;
  lighting?: string;
  environmentIntensity?: number;
  environmentAssetPath?: string;
  previewAngleDeg?: number;
  previewPassiveAngleDeg?: number;
  onPreviewAngleChange?: (value: number) => void;
  onPreviewPassiveAngleChange?: (value: number) => void;
  onSceneReady?: (key: string) => void;
  onSceneError?: (key: string, error: string) => void;
  widthMm: number;
  heightMm: number;
  colour: string;
  // Resolved hex ('#rrggbb') of a customer-entered NCS/RAL code; overrides the
  // placeholder tone of colour_custom_ncs / colour_custom_ral when present.
  customColourHex?: string | null;
  forceSolidColour?: boolean;
  design: string;
  glass: string;
  handing: string;
  opening: string;
  threshold: string;
  hinges?: number;
  family?: string;
  sidelight?: string;
  sidelightWidthMm?: number;
  sidelightHeightMm?: number;
  // Per-side sidelight specs (new API). When absent, the legacy sidelight/
  // sidelightWidthMm/sidelightHeightMm props are used for both sides.
  leftSidelight?: SidelightSpec | null;
  rightSidelight?: SidelightSpec | null;
  // Waternose visibility overrides the workspace's enabled flag when supplied.
  waternose?: boolean;
  // Double-door leaf widths (mm) — used when design === 'design_double';
  // equal split when absent.
  leafLeftWidthMm?: number;
  leafRightWidthMm?: number;
  leafWindowLayout?: 'none' | 'three_windows';
  // Explicit mm geometry for the development editor and future design presets.
  leafGeometry?: {
    hardware?: { lockType?: 'ASSA' | '3PL'; hinges?: 2 | 3 };
    cottagePattern?: 'vertical' | 'chevron';
    thicknessMm?: number;
    topProfileMm?: number[][];
    waternose?: { enabled?: boolean; anchor?: string; offsetMm?: number; profileMm?: number[][] };
    centreMoulding?: boolean;
    grooves?: { count?: number; direction?: string; side?: string; widthMm?: number; depthMm?: number };
    frame?: { profileMm?: number[][]; leafInsetMm?: number };
    thresholds?: Record<string, { profiles?: number[][][]; leafBottomMm?: number }>;
    openings?: { xMm?: number; yMm?: number; widthMm: number; heightMm: number;
      anchorX?: LeafAnchor; anchorY?: LeafAnchor }[];
    windowLayout?: { mode: string; spacing?: string; gapMm?: number; referenceMm?: number;
      mainAnchor?: LeafAnchor; crossAnchor?: LeafAnchor;
      startMargin?: { value: number; unit: string }; endMargin?: { value: number; unit: string } };
    sideProfileMm?: number[][];
    bottomProfileMm?: number[][];
    cassette?: typeof CASSETTE;
  };
}

// Imperative API for the AI visualisation flow — renders the current
// configuration front-on with an orthographic camera into a transparent PNG.
export interface DoorPreviewHandle {
  captureFrontView: () => Promise<string | null>;
  captureRender: (settings: typeof DEFAULT_RENDER & { sceneKey?: string; signal?: AbortSignal }) => Promise<Blob>;
}

export interface SidelightSpec {
  widthMm?: number;
  heightMm?: number;
  glassType?: string;   // 'CLEAR' | 'FROSTED' | ...
  layout?: string;      // 'FULL_GLASS' | 'ONE_HORIZONTAL_DIVIDER' | 'TWO_HORIZONTAL_DIVIDERS' | 'CUSTOM'
}

// ── Dev / plumbing ─────────────────────────────────────────────────────────
const MODEL_BASE_URL = `${process.env.PUBLIC_URL || ''}/models`;
const MODEL_ASSET_VERSION = '20261013-1';
const TEXTURE_BASE_URL = `${process.env.PUBLIC_URL || ''}/textures`;
const ENV_BASE_URL = `${process.env.PUBLIC_URL || ''}/env`;
// Neutral daylight HDRI (Poly Haven, CC0) — gives the glass real sky
// reflections instead of the procedural studio fallback.
const ENV_HDRIS: Record<string, string> = { daylight: `${ENV_BASE_URL}/sky.hdr`, studio: `${ENV_BASE_URL}/studio.hdr` };
// Re-created on every hot reload — used in effect deps so the Three.js
// scene rebuilds after code edits without a manual F5.
const DEV_BUILD = process.env.NODE_ENV === 'development' ? Date.now() : 0;
// Show each placed part's pivot as RGB axes when explicitly enabled.
const SHOW_PIVOTS = false;

// ── Door colour finishes (hex / PBR) ───────────────────────────────────────
const COLOUR_MAT: Record<string, { color: number; roughness: number; metalness: number }> = {
  colour_untreated: { color: 0xc9a96f, roughness: 0.55, metalness: 0.05 },
  colour_white: { color: 0xf1f0ea, roughness: 0.35, metalness: 0.05 },       // RAL 9010
  colour_offwhite: { color: 0xf3efe6, roughness: 0.38, metalness: 0.05 },     // NCS S 0502-Y
  colour_black: { color: 0x141619, roughness: 0.28, metalness: 0.08 },        // RAL 9005
  colour_anthracite: { color: 0x373f43, roughness: 0.32, metalness: 0.1 },    // RAL 7016
  colour_brown: { color: 0x442f29, roughness: 0.4, metalness: 0.05 },         // RAL 8017
  colour_custom_ncs: { color: 0x7a8699, roughness: 0.4, metalness: 0.05 },
  colour_custom_ral: { color: 0x7a8699, roughness: 0.4, metalness: 0.05 }
};

const resolveColourMat = (colour: string, customHex?: string | null) => {
  const base = COLOUR_MAT[colour] || COLOUR_MAT.colour_untreated;
  if (customHex && /^#[0-9a-f]{6}$/i.test(customHex) &&
      (colour === 'colour_custom_ncs' || colour === 'colour_custom_ral')) {
    return { ...base, color: parseInt(customHex.slice(1), 16) };
  }
  return base;
};

// ── Texture tiling (repeats per metre unless noted) ────────────────────────
const OAK_TEX_SCALE = 2;
// Pine on painted surfaces — untreated finish; _HORIZ is the 90°-rotated
// variant for PaintedHoriz parts so horizontal rails can use a different
// texture density.
const PINE_TEX_SCALE = 1;
const PINE_TEX_SCALE_HORIZ = 1;
// MDF on the 18_DEG leaf face when untreated.
const MDF_TEX_SCALE = 1;
// Timber leaf grain and panel-joint UV scales live in timberLeafSurface.js.

// ── GLB model mapping ──────────────────────────────────────────────────────
// GLB part names per leaf system: "60mm" = Smartline, "40mm" = 18Deg/Cottage.
// 60mm bottom options: Oak / alu. 40mm bottom options: Oak / Pine.
const partNames = (p: string) => ({
  bottomLeftOak: `${p}_bottomleft_Oak`,
  bottomLeftAlu: `${p}_bottomleft_alu`,
  bottomLeftPine: `${p}_bottomleft_Pine`,
  topLeft: `${p}_topleft`,
  leftBar: `${p}_Left`,
  topBar: `${p}_top`,
  bottomOak: `${p}_Oak`,
  bottomAlu: `${p}_alu`,
  bottomPine: `${p}_Pine`,
  leafTL: `${p}_DoorTopLeft`,
  leafTop: `${p}_DoorTop`,
  leafLeft: `${p}_DoorLeft`,
  leafBL: `${p}_DoorBottomLeft`,
  leafBottom: `${p}_DoorBottom`,
  leafMid: `${p}_DoorMiddleFill`,
  waternose: `${p}_waternose`
});
// Timber families use the 40 mm construction and matching source part set.
const FAMILY_PREFIX: Record<string, string> = { '18_DEG': '40mm', COTTAGE: '40mm', MERANTI: '40mm' };
// Sidelight glazing beads — unprefixed, authored on the leaf's 10x10 module
// grid (same node positions as the 60mm_Door* parts), so they are laid out
// around the sidelight glass exactly like a leaf inside its frame.
const LIGHT_PART_NAMES = {
  bl: 'Light_BottomLeft',
  tl: 'Light_TopLeft',
  left: 'Light_Left',
  top: 'Light_Top',
  bottom: 'Light_Bottom'
} as const;
// GLB materials rendered as the gradient shadow overlay (see Gradient.webp).
// 'Shadow' = the frame gap shadow (coplanar with the frame's paint),
// '08 - Default' = the leaf edge shadow strips (3ds Max slot 08).
const SHADOW_MATERIAL_NAMES = ['Shadow', '08 - Default'];
const LOCK_NAME = 'Lock';
const LOCKCOVER_NAME = 'LockCover';
const HANDLE_NAME = 'Handle';
const HINGE_NAME = 'Hinge';

// ── Leaf geometry ──────────────────────────────────────────────────────────
// The leaf sits inside the jamb: inset 29 mm from the outline so its edges
// tuck behind the frame pieces.
const LEAF_Z = -0.0;
// Leaf thickness is 60 mm — its front face sits at this Z.
const LEAF_FACE_Z = 0.0075;
const COTTAGE_LIST_OFFSET_X = 0.0;
const COTTAGE_LIST_OFFSET_Y = 0.0;

// ── Hardware placement ─────────────────────────────────────────────────────
// Heights measured from the door's bottom edge.
const LOCK_Y = 1.07;
const LOCK_COVER_Y = 1.015; // 1070 - 55
const HANDLE_Y = 1.125;     // 1070 + 55
// Handle and lock escutcheon share a vertical line 50 mm in from the leaf edge.
const HANDLE_X_INSET = 0.05;
// Hinge positions — distance in from the leaf's top/bottom edge.
// Hinge depth — near the leaf's front face.
const HINGE_Z = LEAF_FACE_Z - 0.073;
// Extra shift toward the jamb (past the leaf edge).
const HINGE_JAMB_OFFSET = 0.001;

// ── Sidelights ─────────────────────────────────────────────────────────────
// Sidelight default width (overridable via the sidelight spec props).
const SIDELIGHT_W = 0.3;
// Timber divider bar thickness (m) and glass pane inset/thickness (m).
const SL_BAR = 0.04;
const SL_GLASS_INSET = 0.05;
const SL_GLASS_T = 0.006;
// Extra drop (m) of the glass pane's bottom edge below the standard inset.
const SL_GLASS_DROP = 0.01;

const LAYOUT_DIVIDER_COUNT: Record<string, number> = {
  FULL_GLASS: 0,
  ONE_HORIZONTAL_DIVIDER: 1,
  TWO_HORIZONTAL_DIVIDERS: 2
};

const slGlassMaterial = (glassType?: string) => {
  const frosted = (glassType || 'CLEAR').toUpperCase() !== 'CLEAR';
  return new THREE.MeshPhysicalMaterial({
    color: frosted ? 0xe8edf2 : 0xd4e6f2,
    roughness: frosted ? 0.5 : 0.05,
    metalness: frosted ? 0.35 : 0.6,
    transparent: true,
    opacity: frosted ? 0.82 : 0.4,
    depthWrite: false
  });
};

const Spinner = () => (
  <div style={{
    position: 'absolute', inset: 0,
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    color: '#9ca3af', gap: '0.75rem', pointerEvents: 'none'
  }}>
    <div style={{
      width: 32, height: 32,
      border: '3px solid rgba(223,169,38,0.25)',
      borderTopColor: '#dfa926',
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite'
    }} />
    <span style={{ fontSize: '0.8rem' }}>Loading 3D preview…</span>
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

interface PartSizes {
  corner: number; // max side of corner cube
  barWidth: number;
  barHeight: number;
  topBarWidth: number;
  topBarHeight: number;
  bottomBarWidth: number;
  aluBarWidth: number;
  leafW: number;
  leafH: number;
}

// Planar-project UVs onto every mesh in `part`, in DOOR-LOCAL space: the leaf
// is a flat panel, so the texture projects straight through it — u = x,
// v = y relative to the door root. Edge faces continue the front face's
// pattern seamlessly. Working in door-local space (inverse of the parent's
// matrixWorld) makes the projection ordering-independent — unaffected by
// any root transform or the leaf swing rotation. `center` is the
// door-local point the texture is centred on (the DoorMiddleFill centre).
const projectWorldUVs = (part: THREE.Object3D, center: THREE.Vector3, scale: number, channel = 'uv') => {
  part.updateWorldMatrix(true, true);
  const toDoorLocal = new THREE.Matrix4();
  if (part.parent) toDoorLocal.copy(part.parent.matrixWorld).invert();
  part.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (!mesh.isMesh) return;
    // Shadow strips keep their authored UVs — the gradient is laid out on them.
    if ((mesh.material as THREE.Material)?.userData?._shadow) return;
    // Cloned parts share the template geometry — clone before writing UVs.
    // BufferGeometry.clone() shares the userData object with the source, so
    // give the clone its own — otherwise the flag lands on the template and
    // later clones (e.g. the mirrored edge strips) write UVs into one shared
    // geometry, and the last projection wins for every copy.
    if (!(mesh.geometry as any).userData._uvCloned) {
      const geo = mesh.geometry.clone();
      geo.userData = { ...geo.userData, _uvCloned: true };
      mesh.geometry = geo;
    }
    const geo = mesh.geometry as THREE.BufferGeometry;
    const pos = geo.getAttribute('position');
    if (!pos) return;
    let uv = geo.getAttribute(channel) as THREE.BufferAttribute;
    if (!uv) {
      uv = new THREE.BufferAttribute(new Float32Array(pos.count * 2), 2);
      geo.setAttribute(channel, uv);
    }
    const v = new THREE.Vector3();
    for (let i = 0; i < pos.count; i++) {
      v.fromBufferAttribute(pos, i).applyMatrix4(mesh.matrixWorld).applyMatrix4(toDoorLocal);
      uv.setXY(i, (v.x - center.x) * scale + 0.5, (v.y - center.y) * scale + 0.5);
    }
    uv.needsUpdate = true;
  });
};

// Applies a world-space box/triplanar-mapped texture: samples along all three
// world axes blended by face normal, so the grain keeps a constant real-world
// scale on every face regardless of how much the mesh is stretched.
// `rotate` turns the sampled image 90° on every face — used for PaintedHoriz
// parts whose grain must run horizontally.
const applyWorldMap = (mat: THREE.MeshStandardMaterial, tex: THREE.Texture, scale: number, rotate = false) => {
  mat.map = tex;
  mat.color.setHex(0xffffff);
  const s = scale.toFixed(4);
  const px = rotate ? 'vec2(-vWPos.y, vWPos.z)' : 'vWPos.zy';
  const py = rotate ? 'vec2(-vWPos.z, vWPos.x)' : 'vWPos.xz';
  const pz = rotate ? 'vec2(-vWPos.y, vWPos.x)' : 'vWPos.xy';
  mat.onBeforeCompile = (shader) => {
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vWPos;\nvarying vec3 vWNorm;')
      .replace('#include <project_vertex>', `#include <project_vertex>
vWPos = (modelMatrix * vec4(transformed, 1.0)).xyz;
vWNorm = normalize(mat3(modelMatrix) * objectNormal);`);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vWPos;\nvarying vec3 vWNorm;')
      .replace('#include <map_fragment>', `#ifdef USE_MAP
  vec3 bw = pow(abs(normalize(vWNorm)), vec3(4.0));
  bw /= (bw.x + bw.y + bw.z);
  vec4 tx = texture2D(map, ${px} * ${s});
  vec4 ty = texture2D(map, ${py} * ${s});
  vec4 tz = texture2D(map, ${pz} * ${s});
  diffuseColor *= tx * bw.x + ty * bw.y + tz * bw.z;
#endif`);
  };
  mat.needsUpdate = true;
  // onBeforeCompile source is identical for every call (scale/rotate are
  // interpolated inside the closure), so without a distinct cache key three.js
  // would reuse whichever variant compiled first for all of them.
  mat.customProgramCacheKey = () => `worldmap:${scale}:${rotate ? 1 : 0}`;
};

const DoorPreviewGLB = forwardRef<DoorPreviewHandle, Props>((props, ref) => {
  const { widthMm, heightMm, colour, customColourHex, design, glass, handing, opening, threshold, hinges = 3, family, sidelight, sidelightWidthMm, sidelightHeightMm, leftSidelight, rightSidelight, waternose, leafLeftWidthMm, leafRightWidthMm, leafWindowLayout = 'none', leafGeometry } = props;
  const cottagePattern = leafGeometry?.cottagePattern ?? 'chevron';
  const containerRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [useFallback, setUseFallback] = useState(false);
  const failureReasonRef = useRef('Could not load the 3D model.');
  const captureBusyRef = useRef(false);
  const resizePreviewRef = useRef<(() => void) | null>(null);
  const sceneBuiltKeyRef = useRef<string | null>(null);
  const sceneCallbacksRef = useRef({ ready: props.onSceneReady, error: props.onSceneError });
  const freezeAutoFitRef = useRef(props.freezeAutoFit);
  freezeAutoFitRef.current = props.freezeAutoFit;
  sceneCallbacksRef.current = { ready: props.onSceneReady, error: props.onSceneError };
  const renderSettingsRef = useRef(props.renderSettings);
  renderSettingsRef.current = props.renderSettings;
  const viewportAORef = useRef(props.viewportAO);
  viewportAORef.current = props.viewportAO;
  const aoSettingsRef = useRef(props.aoSettings);
  aoSettingsRef.current = props.aoSettings;
  const lightingRef = useRef(props.lighting);
  lightingRef.current = props.lighting;
  const intensityRef = useRef(props.environmentIntensity);
  intensityRef.current = props.environmentIntensity;
  const environmentAssetPathRef = useRef(props.environmentAssetPath);
  environmentAssetPathRef.current = props.environmentAssetPath;
  const lightingDirtyRef = useRef(true);
  const invalidateRef = useRef<(() => void) | null>(null);
  // React changes (including rebuilt geometry/materials) need one fresh frame.
  useEffect(() => { invalidateRef.current?.(); });
  const applyLightingRef = useRef<((mode: string, intensity: number) => void) | null>(null);
  const ensureEnvironmentRef = useRef<((mode: string) => Promise<void>) | null>(null);
  const renderCameraRef = useRef<THREE.OrthographicCamera | null>(null);
  const lastRenderSettingsRef = useRef<typeof DEFAULT_RENDER | undefined>();
  const [openAngle, setOpenAngle] = useState(0);
  const [passiveAngle, setPassiveAngle] = useState(0);
  useEffect(() => { if (props.previewAngleDeg !== undefined) setOpenAngle(props.previewAngleDeg); }, [props.previewAngleDeg]);
  useEffect(() => { if (props.previewPassiveAngleDeg !== undefined) setPassiveAngle(props.previewPassiveAngleDeg); }, [props.previewPassiveAngleDeg]);
  const anglesRef = useRef({ active: 0, passive: 0 });
  anglesRef.current = { active: openAngle, passive: passiveAngle };
  const leafPivotsRef = useRef<{ group: THREE.Group; sign: number; active: boolean }[]>([]);

  const sceneRef = useRef<THREE.Scene | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const doorRootRef = useRef<THREE.Group | null>(null);

  // Loaded GLB templates and measured sizes, keyed by leaf-system prefix.
  const setsRef = useRef<Record<string, Record<string, THREE.Object3D>>>({});
  const sizesByPrefixRef = useRef<Record<string, PartSizes>>({});
  const cottageTexRef = useRef<THREE.Texture | null>(null);
  const cottageOcclusionTexRef = useRef<THREE.Texture | null>(null);
  const cottageDiffuseTexRef = useRef<THREE.Texture | null>(null);
  const merantiTexRef = useRef<THREE.Texture | null>(null);
  const verticalPanelNormalRef = useRef<THREE.Texture | null>(null);
  const verticalPanelOcclusionRef = useRef<THREE.Texture | null>(null);
  const pineTexRef = useRef<THREE.Texture | null>(null);
  const pineTexHorizRef = useRef<THREE.Texture | null>(null);
  const mdfTexRef = useRef<THREE.Texture | null>(null);
  const mdfTexHorizRef = useRef<THREE.Texture | null>(null);
  const lockRef = useRef<THREE.Object3D | null>(null);
  const lockCoverRef = useRef<THREE.Object3D | null>(null);
  const handleRef = useRef<THREE.Object3D | null>(null);
  const hingeRef = useRef<THREE.Object3D | null>(null);
  // Mobile auto-framing: set by the init effect, used after every rebuild.
  const fitToBoxRef = useRef<((box: THREE.Box3) => void) | null>(null);
  const lastSetBoxRef = useRef<THREE.Box3 | null>(null);
  const lightPartsRef = useRef<Partial<Record<keyof typeof LIGHT_PART_NAMES, THREE.Object3D>>>({});
  const framePartsRef = useRef<THREE.Object3D[]>([]);
  const sidelightPartsRef = useRef<THREE.Object3D[]>([]);
  const leafPartRef = useRef<THREE.Mesh | null>(null);
  const leafBuilderRef = useRef<Awaited<ReturnType<typeof loadDoorLeafBuilder>> | null>(null);
  const proceduralLeafMatRef = useRef<THREE.MeshStandardMaterial | null>(null);
  const proceduralPartsRef = useRef<THREE.Mesh[]>([]);
  const colourableMatsRef = useRef<THREE.MeshStandardMaterial[]>([]);
  // Latest custom tone, read by the scene rebuild without retriggering it.
  const customHexRef = useRef<string | null | undefined>(customColourHex);
  customHexRef.current = customColourHex;
  const cottageLeafMatsRef = useRef<THREE.MeshStandardMaterial[]>([]);
  const rafRef = useRef<number | null>(null);

  // Transparent front-view PNG of the exact configured door — used by the AI
  // facade visualiser. An orthographic camera removes perspective distortion
  // so the image can be warped into the detected opening deterministically.
  useImperativeHandle(ref, () => ({
    captureRender: async (settings) => {
      validateRenderSettings(settings);
      const renderer = rendererRef.current, scene = sceneRef.current, root = doorRootRef.current;
      if (!renderer || !scene || !root || !root.children.length ||
          (settings.sceneKey !== undefined && sceneBuiltKeyRef.current !== settings.sceneKey)) throw new Error('The 3D scene is not ready yet.');
      if (captureBusyRef.current) throw new Error('A render is already running.');
      if (settings.signal?.aborted) throw new DOMException('Renderdus katkestatud.', 'AbortError');
      const gl = renderer.getContext();
      const pixels = renderPixelSize(settings);
      const maxSide = Math.min(renderer.capabilities.maxTextureSize, gl.getParameter(gl.MAX_RENDERBUFFER_SIZE));
      if (pixels.width > maxSide || pixels.height > maxSide) throw new Error(`This device supports up to ${maxSide} px per side including supersampling.`);
      const oldSize = renderer.getSize(new THREE.Vector2()), oldRatio = renderer.getPixelRatio();
      const oldBackground = scene.background;
      const oldClear = renderer.getClearColor(new THREE.Color()), oldAlpha = renderer.getClearAlpha();
      const rotations = leafPivotsRef.current.map(({ group }) => group.rotation.y);
      captureBusyRef.current = true;
      let captureAO: ReturnType<typeof createDoorPostprocessing> | undefined;
      try {
        await loadAntialiasing();
        if (settings.signal?.aborted) throw new DOMException('Renderdus katkestatud.', 'AbortError');
        await ensureEnvironmentRef.current?.(settings.environment || 'daylight');
        applyLightingRef.current?.(settings.environment || 'daylight', settings.environmentIntensity ?? 1);
        leafPivotsRef.current.forEach(({ group, active, sign }) => {
          group.rotation.y = THREE.MathUtils.degToRad(active ? settings.angleDeg : settings.passiveAngleDeg) * sign;
        });
        root.updateMatrixWorld(true);
        const camera = createRenderCamera(new THREE.Box3().setFromObject(root), settings);
        scene.background = settings.background === 'transparent' ? null : new THREE.Color(settings.background);
        renderer.setClearColor(0x000000, 0);
        renderer.setPixelRatio(1); renderer.setSize(pixels.width, pixels.height, false);
        renderer.clear();
        if (settings.ambientOcclusion || (settings.antialias && settings.antialias !== 'msaa')) {
          captureAO = createDoorPostprocessing(renderer, scene, camera, { ambientOcclusion: !!settings.ambientOcclusion,
            antialias: settings.antialias || 'msaa', quality: 'Ultra', accumulate: true, aoSettings: settings.aoSettings });
          await captureAO.renderForExport(settings.signal);
        } else renderer.render(scene, camera);
        let output = renderer.domElement;
        if (pixels.scale > 1) {
          output = document.createElement('canvas'); output.width = settings.width; output.height = settings.height;
          const context = output.getContext('2d');
          if (!context) throw new Error('Canvas 2D is unavailable for downsampling.');
          context.imageSmoothingEnabled = true; context.imageSmoothingQuality = 'high';
          context.drawImage(renderer.domElement, 0, 0, settings.width, settings.height);
        }
        const blob = await new Promise<Blob>((resolve, reject) => output.toBlob(
          (result) => result ? resolve(result) : reject(new Error('Image encoding failed.')),
          settings.format === 'jpeg' ? 'image/jpeg' : 'image/png', 0.95));
        if (settings.signal?.aborted) throw new DOMException('Renderdus katkestatud.', 'AbortError');
        return blob;
      } finally {
        captureAO?.dispose();
        renderer.setRenderTarget(null);
        leafPivotsRef.current.forEach(({ group }, i) => { if (rotations[i] !== undefined) group.rotation.y = rotations[i]; });
        scene.background = oldBackground;
        applyLightingRef.current?.(lightingRef.current || renderSettingsRef.current?.environment || 'daylight',
          intensityRef.current ?? renderSettingsRef.current?.environmentIntensity ?? 1);
        renderer.setClearColor(oldClear, oldAlpha);
        renderer.setPixelRatio(oldRatio); renderer.setSize(oldSize.x, oldSize.y, false);
        captureBusyRef.current = false;
        resizePreviewRef.current?.();
      }
    },
    captureFrontView: async () => {
      const renderer = rendererRef.current;
      const scene = sceneRef.current;
      const root = doorRootRef.current;
      const container = containerRef.current;
      if (!renderer || !scene || !root || !container || !root.children.length) return null;

      const box = new THREE.Box3().setFromObject(root);
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());
      if (!size.x || !size.y) return null;

      const pad = 0.015;
      const halfW = size.x / 2 + pad;
      const halfH = size.y / 2 + pad;
      const ortho = new THREE.OrthographicCamera(-halfW, halfW, halfH, -halfH, 0.01, 20);
      // The source model exterior is -Z, matching the Front render view.
      ortho.position.set(center.x, center.y, box.min.z - 4);
      ortho.lookAt(center.x, center.y, center.z);
      ortho.updateProjectionMatrix();

      const outW = 1200;
      const outH = Math.max(1, Math.round(outW * (halfH / halfW)));
      const prevW = container.clientWidth;
      const prevH = container.clientHeight;
      const prevPixelRatio = renderer.getPixelRatio();
      try {
        renderer.setPixelRatio(1);
        renderer.setSize(outW, outH, false);
        renderer.render(scene, ortho);
        const dataUrl = renderer.domElement.toDataURL('image/png');
        return dataUrl;
      } finally {
        renderer.setPixelRatio(prevPixelRatio);
        renderer.setSize(prevW, prevH, false);
        invalidateRef.current?.();
      }
    }
  }), []);

  useEffect(() => {
    if (useFallback || status === 'error') sceneCallbacksRef.current.error?.(props.sceneKey || '', failureReasonRef.current);
  }, [status, useFallback, props.sceneKey]);

  // Fast Refresh preserves state — retry the GLB after every code edit
  // instead of staying stuck on the CSS fallback until a manual F5.
  useEffect(() => {
    setUseFallback(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [DEV_BUILD]);

  useEffect(() => {
    if (useFallback) return;
    const container = containerRef.current;
    if (!container) return;

    const loader = new GLTFLoader();
    let cancelled = false;

    const scene = new THREE.Scene();
    scene.background = null;
    sceneRef.current = scene;

    const width = container.clientWidth || 320;
    const height = container.clientHeight || 400;
    const camera = new THREE.PerspectiveCamera(45, width / height, 0.01, 100);
    scene.add(camera);

    let renderer: THREE.WebGLRenderer;
    try { renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true }); }
    catch (error) {
      failureReasonRef.current = error instanceof Error ? error.message : 'Could not create the 3D graphics context.';
      sceneRef.current = null;
      setStatus('error');
      setUseFallback(true);
      return;
    }
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = false;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;
    rendererRef.current = renderer;
    container.appendChild(renderer.domElement);
    const leafBuilderPromise = loadDoorLeafBuilder();

    const invalidate = () => {
      if (!cancelled && rafRef.current === null) rafRef.current = requestAnimationFrame(animate);
    };
    invalidateRef.current = invalidate;

    // Both Studio and the public configurator share this AO pipeline.
    let viewportAO: ReturnType<typeof createDoorPostprocessing> | undefined;
    let interacting = false, resumeAOAt = 0;
    let postprocessingReady = false;
    const editorLighting = props.viewportAO !== undefined;
    const postprocessingPromise = editorLighting ? loadAntialiasing().then(() => { postprocessingReady = true; invalidate(); }) : Promise.resolve();
    const renderViewport = (viewCamera: THREE.Camera) => {
      if (!viewportAORef.current || !postprocessingReady) {
        viewportAO?.dispose(); viewportAO = undefined;
        renderer.render(scene, viewCamera);
        return;
      }
      // Bypass all postprocessing while dragging, then resume 500 ms after
      // release. Damping does not postpone the timer; a new drag pauses it again.
      if (interacting || performance.now() < resumeAOAt) {
        renderer.render(scene, viewCamera);
        return;
      }
      if (viewportAO && viewportAO.cameraType !== viewCamera.type) {
        viewportAO.dispose(); viewportAO = undefined;
      }
      const settings = aoSettingsRef.current || renderSettingsRef.current?.aoSettings || DEFAULT_AO_SETTINGS;
      const quality = settings.quality === 'auto' ? (window.innerWidth <= 900 ? 'Performance' : 'Medium') : settings.quality;
      if (!viewportAO) viewportAO = createDoorPostprocessing(renderer, scene, viewCamera,
        { ambientOcclusion: true, quality, aoSettings: settings });
      viewportAO.setAOSettings(settings, quality);
      viewportAO.render(viewCamera);
    };

    // Procedural studio env renders instantly; the HDRI replaces it once
    // streamed so first paint never waits on the ~1.4 MB download.
    const pmremGenerator = new THREE.PMREMGenerator(renderer);
    const room = new RoomEnvironment();
    const fallbackEnv = pmremGenerator.fromScene(room).texture;
    room.dispose();
    scene.environment = fallbackEnv;
    const environments: Record<string, THREE.Texture> = {};
    pmremGenerator.dispose();
    const environmentRequests = new Map<string, Promise<void>>();
    const ensureEnvironment = (mode: string): Promise<void> => {
      if (!environmentRequests.has(mode)) {
        const request = (async () => {
          let objectUrl: string | undefined;
          try {
            const url = ENV_HDRIS[mode] || (objectUrl = URL.createObjectURL((environmentAssetPathRef.current
              ? await readPublicEnvironmentBlob(environmentAssetPathRef.current) : await readEnvironmentBlob(mode)) as Blob));
            const texture = await new RGBELoader().loadAsync(url);
            if (!cancelled) {
              const generator = new THREE.PMREMGenerator(renderer);
              try { environments[mode] = generator.fromEquirectangular(texture).texture; }
              finally { generator.dispose(); }
              lightingDirtyRef.current = true;
              invalidate();
            }
            texture.dispose();
          } finally { if (objectUrl) URL.revokeObjectURL(objectUrl); }
        })();
        environmentRequests.set(mode, request);
      }
      return environmentRequests.get(mode)!;
    };
    ensureEnvironmentRef.current = ensureEnvironment;
    const environmentPromise = Promise.all((editorLighting ? ['daylight', 'studio'] : ['daylight']).map((mode) =>
      ensureEnvironment(mode).catch(() => { /* Studio fallback remains available if a bundled HDRI fails. */ })));

    const ambient = new THREE.AmbientLight(0xffffff, 0.45);
    scene.add(ambient);
    const dir = new THREE.DirectionalLight(0xffffff, 1.2);
    dir.position.set(-7, 7.5, -15);
    dir.castShadow = false;
    scene.add(dir);
    scene.add(dir.target);
    const fill = new THREE.DirectionalLight(0xffffff, 0.35);
    fill.position.set(-2, 2, -2);
    scene.add(fill);
    let lastLighting = '';
    const applyLighting = (mode: string, intensity: number) => {
      if (!environmentRequests.has(mode)) ensureEnvironment(mode).catch((error) => {
        sceneCallbacksRef.current.error?.(sceneBuiltKeyRef.current || '', `HDRI: ${error.message}`);
      });
      scene.environment = environments[mode] || fallbackEnv;
      const lightKey = `${mode}:${intensity}`;
      if (lastLighting === lightKey && !lightingDirtyRef.current) return;
      lastLighting = lightKey; lightingDirtyRef.current = false;
      scene.traverse((object) => {
        const mesh = object as THREE.Mesh;
        const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        materials.forEach((material) => {
          if ((material as THREE.MeshStandardMaterial)?.isMeshStandardMaterial) (material as THREE.MeshStandardMaterial).envMapIntensity = intensity;
        });
      });
      const studio = mode === 'studio';
      ambient.intensity = studio ? 0.12 : 0.45;
      fill.intensity = studio ? 0.15 : 0.35;
      dir.intensity = studio ? 1.5 : 1.2;
    };
    applyLightingRef.current = applyLighting;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.enablePan = true;
    controls.mouseButtons.RIGHT = THREE.MOUSE.PAN;
    controls.panSpeed = 0.8;
    controls.screenSpacePanning = true;
    controls.minDistance = 0.3;
    controls.maxDistance = 5;
    controlsRef.current = controls;
    let initialFitPending = true;
    let resumeAOTimer: ReturnType<typeof setTimeout> | undefined;
    const pauseAO = () => { interacting = true; clearTimeout(resumeAOTimer); invalidate(); };
    const resumeAO = () => {
      interacting = false;
      resumeAOAt = performance.now() + 500;
      clearTimeout(resumeAOTimer);
      resumeAOTimer = setTimeout(() => { resumeAOAt = 0; invalidate(); }, 500);
      invalidate();
    };
    controls.addEventListener('change', invalidate);
    controls.addEventListener('start', pauseAO);
    controls.addEventListener('end', resumeAO);

    // Mobile layout: the preview is a sticky top panel — vertical swipes must
    // scroll the page, so polar rotation is locked to the horizon and the
    // canvas only claims horizontal touch gestures.
    const applyControlMode = () => {
      const mobile = window.innerWidth <= 900;
      controls.minPolarAngle = mobile ? Math.PI / 2 : 0;
      controls.maxPolarAngle = mobile ? Math.PI / 2 : Math.PI;
      controls.enableZoom = !mobile;
      renderer.domElement.style.touchAction = mobile ? 'pan-y' : 'none';
    };
    applyControlMode();

    // Hold H to pan: while held, left-drag pans instead of rotating.
    // Ignored while typing in form fields.
    const setPanMode = (on: boolean) => {
      controls.mouseButtons.LEFT = on ? THREE.MOUSE.PAN : THREE.MOUSE.ROTATE;
      renderer.domElement.style.cursor = on ? 'move' : '';
    };
    const isTyping = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable);
    };
    const onPanKeyDown = (e: KeyboardEvent) => {
      if ((e.key === 'h' || e.key === 'H') && !e.repeat && !isTyping(e)) setPanMode(true);
    };
    const onPanKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'h' || e.key === 'H') setPanMode(false);
    };
    const onPanBlur = () => { setPanMode(false); resumeAO(); };
    window.addEventListener('keydown', onPanKeyDown);
    window.addEventListener('keyup', onPanKeyUp);
    window.addEventListener('blur', onPanBlur);

    const doorRoot = new THREE.Group();
    doorRootRef.current = doorRoot;
    scene.add(doorRoot);

    // Oak texture (optional) — loaded in parallel with the GLB.
    let oakTex: THREE.Texture | null = null;
    const oakTexPromise = new THREE.TextureLoader()
      .loadAsync(`${TEXTURE_BASE_URL}/Oak.webp`)
      .then((t) => {
        t.wrapS = THREE.RepeatWrapping;
        t.wrapT = THREE.RepeatWrapping;
        t.colorSpace = THREE.SRGBColorSpace;
        t.anisotropy = renderer.capabilities.getMaxAnisotropy();
        oakTex = t;
      })
      .catch(() => { /* texture not present yet — Oak keeps its flat colour */ });

    // Cottage leaf normal map (optional) — board relief on the 40mm leaf.
    const cottageNormalPromise = new THREE.TextureLoader()
      .loadAsync(`${TEXTURE_BASE_URL}/CottageNormal.webp`)
      .then((t) => {
        t.wrapS = THREE.RepeatWrapping;
        t.wrapT = THREE.RepeatWrapping;
        t.anisotropy = renderer.capabilities.getMaxAnisotropy();
        cottageTexRef.current = t;
      })
      .catch(() => { /* texture not present yet — leaf stays smooth */ });

    const cottageOcclusionPromise = new THREE.TextureLoader()
      .loadAsync(`${TEXTURE_BASE_URL}/CottageNormalOcclusion.webp`)
      .then((t) => {
        t.wrapS = THREE.RepeatWrapping;
        t.wrapT = THREE.RepeatWrapping;
        t.anisotropy = renderer.capabilities.getMaxAnisotropy();
        cottageOcclusionTexRef.current = t;
      })
      .catch(() => { /* texture not present yet — Cottage normal remains disabled */ });

    // Cottage leaf diffuse (optional) — same authored panel size as the normal
    // map, so it shares the world-projected uv channel.
    const cottageDiffusePromise = new THREE.TextureLoader()
      .loadAsync(`${TEXTURE_BASE_URL}/CottageDiffuse.webp`)
      .then((t) => {
        t.wrapS = THREE.RepeatWrapping;
        t.wrapT = THREE.RepeatWrapping;
        t.colorSpace = THREE.SRGBColorSpace;
        t.anisotropy = renderer.capabilities.getMaxAnisotropy();
        cottageDiffuseTexRef.current = t;
      })
      .catch(() => { /* texture not present yet */ });

    // Meranti leaf textures (optional) — diffuse + normal.
    const merantiTexPromise = new THREE.TextureLoader()
      .loadAsync(`${TEXTURE_BASE_URL}/Meranti.webp`)
      .then((t) => {
        t.wrapS = THREE.RepeatWrapping;
        t.wrapT = THREE.RepeatWrapping;
        t.colorSpace = THREE.SRGBColorSpace;
        t.anisotropy = renderer.capabilities.getMaxAnisotropy();
        merantiTexRef.current = t;
      })
      .catch(() => { /* texture not present yet */ });
    const verticalPanelNormalPromise = new THREE.TextureLoader()
      .loadAsync(`${TEXTURE_BASE_URL}/merantilines.webp`)
      .then((t) => {
        t.wrapS = THREE.RepeatWrapping;
        t.wrapT = THREE.RepeatWrapping;
        t.anisotropy = renderer.capabilities.getMaxAnisotropy();
        t.channel = 1; // sample from uv1 so the normal map can tile at its own scale
        verticalPanelNormalRef.current = t;
      })
      .catch(() => { /* texture not present yet */ });
    const verticalPanelOcclusionPromise = new THREE.TextureLoader()
      .loadAsync(`${TEXTURE_BASE_URL}/merantilinesocclusion.webp`)
      .then((t) => {
        t.wrapS = THREE.RepeatWrapping;
        t.wrapT = THREE.RepeatWrapping;
        t.anisotropy = renderer.capabilities.getMaxAnisotropy();
        t.channel = 1;
        verticalPanelOcclusionRef.current = t;
      })
      .catch(() => { /* Vertical Cottage relief stays disabled without matching AO. */ });

    // Raw pine texture for the "untreated" finish on the 40 mm families —
    // applied world-aligned so the grain follows the painted surfaces.
    const pineTexPromise = new THREE.TextureLoader()
      .loadAsync(`${TEXTURE_BASE_URL}/PineTexture.webp`)
      .then((t) => {
        t.wrapS = THREE.RepeatWrapping;
        t.wrapT = THREE.RepeatWrapping;
        t.colorSpace = THREE.SRGBColorSpace;
        t.anisotropy = renderer.capabilities.getMaxAnisotropy();
        pineTexRef.current = t;
        // Rotated clone for PaintedHoriz leaf parts (e.g. waternose) — they go
        // through the UV-projected leaf path, so the rotation is baked into a
        // second texture instead of the world-map shader.
        const h = t.clone();
        h.center.set(0.5, 0.5);
        h.rotation = Math.PI / 2;
        h.needsUpdate = true;
        pineTexHorizRef.current = h;
      })
      .catch(() => { /* texture not present yet */ });

    // MDF texture — the 18_DEG leaf face when untreated.
    const mdfTexPromise = new THREE.TextureLoader()
      .loadAsync(`${TEXTURE_BASE_URL}/mdf.webp`)
      .then((t) => {
        t.wrapS = THREE.RepeatWrapping;
        t.wrapT = THREE.RepeatWrapping;
        t.colorSpace = THREE.SRGBColorSpace;
        t.anisotropy = renderer.capabilities.getMaxAnisotropy();
        mdfTexRef.current = t;
        const h = t.clone();
        h.center.set(0.5, 0.5);
        h.rotation = Math.PI / 2;
        h.needsUpdate = true;
        mdfTexHorizRef.current = h;
      })
      .catch(() => { /* texture not present yet */ });

    // Shadow overlay texture — greyscale gradient used as an alpha map for the
    // GLB material "08 - Default" (the leaf edge shadow strips): black, unlit,
    // opacity from the image.
    let shadowMat: THREE.MeshBasicMaterial | null = null;
    const shadowTexPromise = new THREE.TextureLoader()
      .loadAsync(`${TEXTURE_BASE_URL}/Gradient.webp`)
      .then((t) => {
        // glTF UVs are authored for flipY = false.
        t.flipY = false;
        t.needsUpdate = true;
        shadowMat = new THREE.MeshBasicMaterial({
          name: 'Shadow',
          color: 0x000000,
          alphaMap: t,
          transparent: true,
          depthWrite: false,
          // coplanar with its Painted base strip — pull it forward in depth
          polygonOffset: true,
          polygonOffsetFactor: -1,
          polygonOffsetUnits: -1
        });
        shadowMat.userData._shadow = true;
      })
      .catch(() => { /* keep the GLB's own Shadow material */ });

    // GLB materials: "Oak" keeps its wood tone, "Painted" takes the chosen
    // door colour, "Alu" gets a metallic finish.
    // Nodes with multiple primitives load as Groups — traverse all meshes.
    const cloneMaterials = (obj: THREE.Object3D) => {
      obj.traverse((child) => {
        const mesh = child as THREE.Mesh;
        if (!mesh.isMesh) return;
        // "08 - Default" → the shared black alpha-mapped shadow material.
        const swap = (m: THREE.Material) =>
          shadowMat && SHADOW_MATERIAL_NAMES.includes(m.name) ? shadowMat : m.clone();
        if (Array.isArray(mesh.material)) {
          mesh.material = mesh.material.map(swap);
        } else {
          mesh.material = swap(mesh.material as THREE.Material);
        }
        const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        mats.forEach((mat: THREE.Material) => {
          // The GLB carries stray all-black COLOR_0 vertex colours on some
          // Painted leaf parts (3ds Max export); GLTFLoader turns on
          // vertexColors for them, which multiplies the paint to black.
          // Vertex colours are never used intentionally — ignore them.
          (mat as THREE.MeshStandardMaterial).vertexColors = false;
          if (mat instanceof THREE.MeshStandardMaterial) {
            // Painted and PaintedHoriz (same paint, horizontal grain in the
            // authored UVs) both take the chosen door colour.
            if (mat.name === 'Painted' || mat.name === 'PaintedHoriz') {
              // PaintedHoriz: same paint, grain authored to run horizontally —
              // the pine world-map is rotated 90° for these.
              (mat as any).userData._horiz = mat.name === 'PaintedHoriz';
              // Leaf Painted mats are tracked separately so the cottage
              // normal map can be toggled when the family changes — the GLB
              // load effect runs once and must not bake in a stale family.
              if (obj.name.includes('Door')) {
                cottageLeafMatsRef.current.push(mat);
              }
              colourableMatsRef.current.push(mat);
            } else if (mat.name === 'Alu') {
              mat.metalness = 0.85;
              mat.roughness = 0.35;
              mat.color.setHex(0xb8bcc2);
            } else if (mat.name === 'Oak') {
              if (oakTex) {
                applyWorldMap(mat, oakTex, OAK_TEX_SCALE);
              }
              mat.metalness = 0.05;
              mat.roughness = 0.55;
            } else if (mat.name === 'Chrome') {
              mat.metalness = 1.0;
              mat.roughness = 0.15;
            } else if (mat.name === 'Brass' || mat.name === 'Bronze') {
              mat.metalness = 0.9;
              mat.roughness = 0.3;
            }
            mat.needsUpdate = true;
          }
        });
        mesh.castShadow = true;
        mesh.receiveShadow = true;
      });

      // "08 - Default" strips are the only surface on the leaf edge, so an
      // alpha overlay alone would show a hole. Give each strip the part's
      // own Painted material as an opaque base and lay the black gradient
      // shadow on top as a child mesh (cloned along with the part).
      if (!shadowMat) return;
      let painted: THREE.Material | null = null;
      const strips: THREE.Mesh[] = [];
      obj.traverse((child) => {
        const mesh = child as THREE.Mesh;
        if (!mesh.isMesh) return;
        if (mesh.material === shadowMat) strips.push(mesh);
        else if (!painted && (mesh.material as THREE.Material).name === 'Painted') painted = mesh.material as THREE.Material;
      });
      strips.forEach((strip) => {
        if (painted) strip.material = painted;
        const overlay = new THREE.Mesh(strip.geometry, shadowMat!);
        overlay.name = `${strip.name}_shadow`;
        overlay.renderOrder = 1;
        strip.add(overlay);
      });
    };

    Promise.all([loader.loadAsync(`${MODEL_BASE_URL}/DoorDynamic.glb?v=${MODEL_ASSET_VERSION}`), leafBuilderPromise])
      .then(async ([gltf, leafBuilder]) => {
        await oakTexPromise;
        await Promise.all([
          environmentPromise,
          postprocessingPromise,
          cottageNormalPromise,
          cottageOcclusionPromise,
          cottageDiffusePromise,
          merantiTexPromise,
          verticalPanelNormalPromise,
          verticalPanelOcclusionPromise,
          pineTexPromise,
          mdfTexPromise,
          shadowTexPromise
        ]);
        if (cancelled) return;
        leafBuilderRef.current = leafBuilder;
        const proceduralMat = new THREE.MeshStandardMaterial({ name: 'ProceduralLeafPaint' });
        proceduralMat.userData.localWoodUV = true;
        proceduralLeafMatRef.current = proceduralMat;
        colourableMatsRef.current.push(proceduralMat);
        const dynamicScene = gltf.scene;

        const sets: Record<string, Record<string, THREE.Object3D>> = {};
        const sizesByPrefix: Record<string, PartSizes> = {};
        for (const prefix of ['60mm', '40mm']) {
          const set: Record<string, THREE.Object3D> = {};
          Object.entries(partNames(prefix)).forEach(([key, name]) => {
            const obj = dynamicScene.getObjectByName(name);
            if (obj) {
              cloneMaterials(obj);
              set[key] = obj;
            }
          });
          // Cottage-only trim strip — unprefixed name, stored on the 40mm set.
          if (prefix === '40mm') {
            const cml = dynamicScene.getObjectByName('CottageMiddleList');
            if (cml) {
              cloneMaterials(cml);
              set.cottageList = cml;
            }
          }
          if (!set.bottomLeftOak || !set.topLeft || !set.leftBar || !set.topBar) continue;
          sets[prefix] = set;

          const cornerSize = new THREE.Box3().setFromObject(set.bottomLeftOak).getSize(new THREE.Vector3());
          const leftBarSize = new THREE.Box3().setFromObject(set.leftBar).getSize(new THREE.Vector3());
          const topBarSize = new THREE.Box3().setFromObject(set.topBar).getSize(new THREE.Vector3());
          const bottomSrc = set.bottomOak || set.bottomPine || set.bottomAlu;
          const bottomBarWidth = bottomSrc
            ? new THREE.Box3().setFromObject(bottomSrc).getSize(new THREE.Vector3()).x
            : topBarSize.x;
          const aluBarWidth = set.bottomAlu
            ? new THREE.Box3().setFromObject(set.bottomAlu).getSize(new THREE.Vector3()).x
            : bottomBarWidth;
          let leafW = 0.1;
          let leafH = 0.1;
          if (set.leafLeft) {
            const leafSize = new THREE.Box3().setFromObject(set.leafLeft).getSize(new THREE.Vector3());
            leafW = leafSize.x;
            leafH = leafSize.y;
          }
          sizesByPrefix[prefix] = {
            corner: Math.max(cornerSize.x, cornerSize.y),
            barWidth: leftBarSize.x,
            barHeight: leftBarSize.y,
            topBarWidth: topBarSize.x,
            topBarHeight: topBarSize.y,
            bottomBarWidth,
            aluBarWidth,
            leafW,
            leafH
          };
        }

        if (!sets['60mm'] && !sets['40mm']) {
          console.warn('Required frame meshes not found in DoorDynamic.glb; falling back to CSS preview.');
          throw new Error('Frame meshes missing');
        }
        setsRef.current = sets;
        sizesByPrefixRef.current = sizesByPrefix;

        [LOCK_NAME, LOCKCOVER_NAME, HANDLE_NAME, HINGE_NAME].forEach((name, i) => {
          const obj = dynamicScene.getObjectByName(name);
          if (obj) cloneMaterials(obj);
          [lockRef, lockCoverRef, handleRef, hingeRef][i].current = obj || null;
        });
        const lightParts: Partial<Record<keyof typeof LIGHT_PART_NAMES, THREE.Object3D>> = {};
        (Object.keys(LIGHT_PART_NAMES) as (keyof typeof LIGHT_PART_NAMES)[]).forEach((key) => {
          const obj = dynamicScene.getObjectByName(LIGHT_PART_NAMES[key]);
          if (obj) {
            cloneMaterials(obj);
            lightParts[key] = obj;
          }
        });
        lightPartsRef.current = lightParts;

        // Orbit, pan and zoom are temporary; saved render-camera presets are separate.
        const fitDist = 2.1 * 1.65;
        camera.position.set(fitDist * 0.55, fitDist * 0.45, -fitDist);
        controls.target.set(0.45, 1.05, 0);
        controls.update();

        setStatus('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('DoorPreviewGLB load error:', err);
        failureReasonRef.current = err instanceof Error ? err.message : 'Could not load the 3D model.';
        setStatus('error');
        setUseFallback(true);
      });

    const animate = () => {
      rafRef.current = null;
      if (captureBusyRef.current) return;
      const renderSettings = renderSettingsRef.current;
      try {
        applyLighting(lightingRef.current || renderSettings?.environment || 'daylight', intensityRef.current ?? renderSettings?.environmentIntensity ?? 1);
        let doorMoving = false;
        leafPivotsRef.current.forEach(({ group, sign, active }) => {
          const angle = renderSettings ? (active ? renderSettings.angleDeg : renderSettings.passiveAngleDeg)
            : (active ? anglesRef.current.active : anglesRef.current.passive);
          const target = THREE.MathUtils.degToRad(angle) * sign;
          const delta = target - group.rotation.y;
          if (!renderSettings && Math.abs(delta) > 0.0001) {
            group.rotation.y += delta * 0.2;
            doorMoving = true;
          } else group.rotation.y = target;
        });
        if (renderSettings && doorRoot.children.length) {
          controls.enabled = false;
          try {
            if (!renderCameraRef.current || lastRenderSettingsRef.current !== renderSettings) {
              doorRoot.updateMatrixWorld(true);
              renderCameraRef.current = createRenderCamera(new THREE.Box3().setFromObject(doorRoot), renderSettings);
              lastRenderSettingsRef.current = renderSettings;
              scene.background = renderSettings.background === 'transparent' ? null : new THREE.Color(renderSettings.background);
            }
            renderViewport(renderCameraRef.current);
            return;
          } catch { /* invalid draft settings retain the interactive preview */ }
        }
        controls.enabled = true;
        const cameraMoving = controls.update();
        renderViewport(camera);
        if (doorMoving || cameraMoving) invalidate();
      } catch (error) {
        failureReasonRef.current = error instanceof Error ? error.message : 'Could not render the 3D model.';
        setStatus('error');
        setUseFallback(true);
      }
    };
    invalidate();

    // Leave breathing room on the first view; preserve subsequent orbit, zoom and pan.
    // Mobile keeps fitting because pinch zoom is disabled there.
    fitToBoxRef.current = (box: THREE.Box3) => {
      if (freezeAutoFitRef.current) return;
      if ((window.innerWidth > 900 && !initialFitPending) || box.isEmpty()) return;
      initialFitPending = false;
      const size = box.getSize(new THREE.Vector3());
      const centre = box.getCenter(new THREE.Vector3());
      const vFov = THREE.MathUtils.degToRad(camera.fov);
      const hFov = 2 * Math.atan(Math.tan(vFov / 2) * camera.aspect);
      const dist = Math.max(
        (size.y / 2) / Math.tan(vFov / 2),
        (size.x / 2) / Math.tan(hFov / 2)
      ) * 1.22 + size.z / 2;
      controls.maxDistance = Math.max(5, dist * 2);
      const dir = camera.position.clone().sub(controls.target);
      if (dir.lengthSq() < 1e-6) dir.set(0, 0, 1);
      dir.normalize();
      controls.target.copy(centre);
      camera.position.copy(centre).addScaledVector(dir, dist);
      controls.update();
    };

    const handleResize = () => {
      if (captureBusyRef.current) return;
      const w = container.clientWidth || 320;
      const h = container.clientHeight || 400;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
      viewportAO?.resize(w, h);
      invalidate();
      applyControlMode();
      if (lastSetBoxRef.current) fitToBoxRef.current?.(lastSetBoxRef.current);
    };
    resizePreviewRef.current = handleResize;
    window.addEventListener('resize', handleResize);
    // The container also changes size without a window resize (mobile
    // layout settling, controls panel height) — follow it directly.
    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(handleResize)
      : null;
    resizeObserver?.observe(container);

    return () => {
      cancelled = true;
      clearTimeout(resumeAOTimer);
      invalidateRef.current = null;
      controls.removeEventListener('change', invalidate);
      controls.removeEventListener('start', pauseAO);
      controls.removeEventListener('end', resumeAO);
      resizePreviewRef.current = null;
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('keydown', onPanKeyDown);
      window.removeEventListener('keyup', onPanKeyUp);
      window.removeEventListener('blur', onPanBlur);
      resizeObserver?.disconnect();
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      controls.dispose();
      viewportAO?.dispose();
      applyLightingRef.current = null;
      ensureEnvironmentRef.current = null;
      fallbackEnv.dispose();
      Object.values(environments).forEach((texture) => texture.dispose());
      renderer.dispose();
      if (renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
      scene.traverse((obj) => {
        const mesh = obj as THREE.Mesh;
        if (mesh.geometry) mesh.geometry.dispose();
        if (mesh.material) {
          const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
          mats.forEach((m) => m.dispose());
        }
      });
      framePartsRef.current = [];
      sidelightPartsRef.current = [];
      setsRef.current = {};
      sizesByPrefixRef.current = {};
      lockRef.current = null;
      lockCoverRef.current = null;
      handleRef.current = null;
      hingeRef.current = null;
      colourableMatsRef.current = [];
      cottageLeafMatsRef.current = [];
      proceduralLeafMatRef.current?.dispose();
      proceduralLeafMatRef.current = null;
      proceduralPartsRef.current = [];
      leafBuilderRef.current = null;
      leafPivotsRef.current = [];
      sceneBuiltKeyRef.current = null;
      renderCameraRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useFallback, DEV_BUILD]);


  // Cottage uses paired panel-joint maps; Meranti's relief is actual geometry.
  useEffect(() => {
    const surface = props.forceSolidColour ? null : timberLeafSurface(family, cottagePattern);
    const maps: Record<string, THREE.Texture | null> = {
      cottageDiffuse: cottageDiffuseTexRef.current, cottageNormal: cottageTexRef.current,
      cottageOcclusion: cottageOcclusionTexRef.current, pine: pineTexRef.current,
      meranti: merantiTexRef.current, verticalNormal: verticalPanelNormalRef.current,
      verticalOcclusion: verticalPanelOcclusionRef.current
    };
    const normal = surface?.normal ? maps[surface.normal] : null;
    const ao = surface?.ao ? maps[surface.ao] : null;
    cottageLeafMatsRef.current.forEach((mat) => {
      mat.map = surface ? maps[surface.diffuse] : null;
      mat.normalMap = normal && ao ? normal : null;
      mat.aoMap = normal && ao ? ao : null;
      mat.aoMapIntensity = 1;
      mat.normalScale.setScalar(surface?.strength ?? 1);
      // A diffuse texture supplies its own colour — keep the tint white.
      if (mat.map) mat.color.setHex(0xffffff);
      mat.needsUpdate = true;
    });
  }, [family, cottagePattern, status, props.forceSolidColour]);

  // Update colour materials.
  useEffect(() => {
    const matSpec = resolveColourMat(colour, customColourHex);
    // Cottage uses natural pine throughout; other timber families use pine
    // when untreated. Leaf faces retain their separate family surface maps.
    const usePine = usesPineTrim(family, colour);
    const leafMats = cottageLeafMatsRef.current;
    colourableMatsRef.current.forEach((mat) => {
      const isLeaf = leafMats.includes(mat);
      if (props.forceSolidColour) {
        mat.map = null; mat.normalMap = null; mat.aoMap = null; mat.color.setHex(matSpec.color);
      } else if (isLeaf) {
        // Leaf parts: Cottage/Meranti keep their family texture — it is never
        // colour-tinted (Cottage only offers untreated anyway). 18_DEG
        // untreated shows raw pine on the projected UVs; otherwise the leaf
        // takes the chosen colour.
        if (family === 'COTTAGE' || family === 'MERANTI') {
          if (mat.map) mat.color.setHex(0xffffff);
        } else if (family === '18_DEG' && colour === 'colour_untreated' && mdfTexRef.current) {
          // 18_DEG untreated leaf face is MDF — PaintedHoriz leaf parts get
          // the rotated clone so the grain stays horizontal.
          const base = mdfTexRef.current;
          const horiz = mdfTexHorizRef.current;
          mat.map = ((mat as any).userData._horiz && horiz) ? horiz : base;
          mat.color.setHex(0xffffff);
        } else {
          if (mat.map === pineTexRef.current || mat.map === pineTexHorizRef.current ||
              mat.map === mdfTexRef.current || mat.map === mdfTexHorizRef.current) mat.map = null;
          mat.color.setHex(matSpec.color);
        }
      } else if (usePine && pineTexRef.current) {
        if (mat.userData.localWoodUV) {
          // Generated frames, waternoses and window rails have millimetre-based
          // UVs following each timber member, including its bevelled profile.
          mat.map = pineTexRef.current;
        } else if (!(mat as any).userData._pineApplied) {
          // Legacy GLB accessories retain their existing world-map shader.
          applyWorldMap(
            mat,
            pineTexRef.current,
            (mat as any).userData._horiz ? PINE_TEX_SCALE_HORIZ : PINE_TEX_SCALE,
            !!(mat as any).userData._horiz
          );
          (mat as any).userData._pineApplied = true;
        } else {
          mat.map = pineTexRef.current;
        }
        mat.color.setHex(0xffffff);
      } else {
        if (mat.userData.localWoodUV && mat.map === pineTexRef.current) mat.map = null;
        // Only clear the map if it still IS the pine texture — the shared
        // 40 mm mats may already carry another texture again.
        if ((mat as any).userData._pineApplied) {
          if (mat.map === pineTexRef.current) mat.map = null;
          (mat as any).userData._pineApplied = false;
        }
        mat.color.setHex(matSpec.color);
      }
      mat.roughness = matSpec.roughness;
      mat.metalness = matSpec.metalness;
      mat.needsUpdate = true;
    });
  }, [colour, customColourHex, status, family, props.forceSolidColour]);

  // Build/rebuild the full frame.
  useEffect(() => {
    const root = doorRootRef.current;
    // Pick the part set for this family (18_DEG uses the 40mm parts);
    // fall back to the 60mm set if the wanted one is missing from the GLB.
    const wanted = FAMILY_PREFIX[family || ''] || '60mm';
    const prefix = setsRef.current[wanted] ? wanted : '60mm';
    const P = setsRef.current[prefix];
    const sizes = sizesByPrefixRef.current[prefix];
    if (!root || !P || !sizes) return;
    const construction = { ...doorFamilyDefaults(family || '60_OUT'), ...leafGeometry };
    construction.waternose = { ...construction.waternose, enabled: waternose ?? construction.waternose.enabled };
    const selectedThreshold = familyThreshold(threshold, family);
    const { frontMm, backMm, thicknessMm } = leafDimensions(construction);

    // Mirror the entire closed assembly for inward doors, including the rebate
    // and sill. Turning only the leaf would swing it through the frame stop.
    root.scale.z = opening === 'inward' ? -1 : 1;
    root.position.z = opening === 'inward' ? (frontMm + backMm) / 1000 : 0;
    root.updateMatrixWorld(true);

    const doorW = widthMm / 1000;
    const doorH = heightMm / 1000;
    const corner = sizes.corner;

    // Clear previous.
    sceneBuiltKeyRef.current = null;
    renderCameraRef.current = null;
    leafPivotsRef.current.forEach(({ group }) => group.removeFromParent());
    leafPivotsRef.current = [];
    framePartsRef.current.forEach((m) => {
      m.removeFromParent();
      m.traverse((c) => {
        const mesh = c as THREE.Mesh;
        if (mesh.isMesh && (mesh.geometry as any)?.userData?._uvCloned) {
          mesh.geometry.dispose();
        }
      });
    });
    framePartsRef.current = [];
    const sidelightMaterials = new Set<THREE.Material>();
    sidelightPartsRef.current.forEach((part) => {
      root.remove(part);
      part.traverse((child) => {
        const mesh = child as THREE.Mesh;
        if (!mesh.isMesh) return;
        mesh.geometry.dispose();
        const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        mats.forEach((mat) => sidelightMaterials.add(mat));
      });
    });
    colourableMatsRef.current = colourableMatsRef.current.filter((mat) => !sidelightMaterials.has(mat));
    sidelightMaterials.forEach((mat) => mat.dispose());
    sidelightPartsRef.current = [];
    if (leafPartRef.current) {
      root.remove(leafPartRef.current);
      leafPartRef.current.geometry.dispose();
      leafPartRef.current = null;
    }
    proceduralPartsRef.current.forEach((mesh) => {
      mesh.removeFromParent();
      mesh.geometry.dispose();
      if (mesh.material !== proceduralLeafMatRef.current) {
        const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        colourableMatsRef.current = colourableMatsRef.current.filter((material) => !materials.includes(material));
        cottageLeafMatsRef.current = cottageLeafMatsRef.current.filter((material) => !materials.includes(material));
        materials.forEach((material) => material.dispose());
      }
    });
    proceduralPartsRef.current = [];

    // Places a cloned part so its bounding-box edges align to the door outline.
    // Pivot-agnostic: geometry offsets inside the GLB don't matter.
    interface Align { minX?: number; maxX?: number; centerX?: number; minY?: number; maxY?: number; centerY?: number }
    const place = (template: THREE.Object3D, sx: number, sy: number, sz: number, rotZ: number, align: Align, z = 0) => {
      const part = template.clone();
      part.position.set(0, 0, 0);
      part.scale.set(sx, sy, sz);
      if (rotZ) part.rotation.z = rotZ;
      part.updateMatrixWorld(true);
      const box = new THREE.Box3().setFromObject(part);
      const c = box.getCenter(new THREE.Vector3());
      let px = 0;
      let py = 0;
      if (align.minX !== undefined) px = align.minX - box.min.x;
      else if (align.maxX !== undefined) px = align.maxX - box.max.x;
      else if (align.centerX !== undefined) px = align.centerX - c.x;
      if (align.minY !== undefined) py = align.minY - box.min.y;
      else if (align.maxY !== undefined) py = align.maxY - box.max.y;
      else if (align.centerY !== undefined) py = align.centerY - c.y;
      part.position.set(px, py, z);
      part.castShadow = true;
      part.receiveShadow = true;
      if (SHOW_PIVOTS) {
        const ax = new THREE.AxesHelper(0.08);
        ax.scale.set(1 / Math.abs(sx), 1 / Math.abs(sy), 1 / Math.abs(sz));
        part.add(ax);
      }
      root.add(part);
      framePartsRef.current.push(part);
      return part;
    };

    // Places a cloned part by its own pivot — for hardware where the GLB
    // pivot (e.g. the handle rosette) defines the mount point.
    const placeAt = (template: THREE.Object3D, sx: number, sy: number, sz: number, x: number, y: number, z: number) => {
      const part = template.clone();
      part.scale.set(sx, sy, sz);
      part.position.set(x, y, z);
      part.castShadow = true;
      part.receiveShadow = true;
      if (SHOW_PIVOTS) {
        const ax = new THREE.AxesHelper(0.08);
        ax.scale.set(1 / Math.abs(sx), 1 / Math.abs(sy), 1 / Math.abs(sz));
        part.add(ax);
      }
      root.add(part);
      framePartsRef.current.push(part);
    };

    const bottomKind = threshold === 'threshold_alu' ? 'Alu'
      : (threshold === 'threshold_pine' || (prefix === '40mm' && threshold !== 'threshold_oak')) ? 'Pine' : 'Oak';
    const bottomLeftMesh = P[`bottomLeft${bottomKind}`] || P.bottomLeftOak;
    const bottomBar = P[`bottom${bottomKind}`];
    const proceduralFrame = !!leafBuilderRef.current && !!proceduralLeafMatRef.current;
    const clearances = assemblyClearances({ ...construction, threshold: selectedThreshold });
    const frameInset = clearances.sideMm / 1000;
    const buildFrame = (w: number, h: number, x = 0) => {
      const generated = leafBuilderRef.current!.frame({ widthMm: w * 1000, heightMm: h * 1000, threshold: selectedThreshold,
        frame: construction.frame, thresholds: construction.thresholds });
      generated.forEach(({ name, material, geometry }) => {
        let mat = proceduralLeafMatRef.current!;
        if (material === 'oak') {
          mat = new THREE.MeshStandardMaterial({ color: 0xb99362, roughness: 0.55 });
          P.bottomOak?.traverse((child) => {
            const m = (child as THREE.Mesh).material as THREE.MeshStandardMaterial;
            if (m?.name === 'Oak' && m.map) mat.map = m.map;
          });
        } else if (material === 'alu') mat = new THREE.MeshStandardMaterial({ color: 0xb8bcc2, metalness: 0.85, roughness: 0.35 });
        const mesh = new THREE.Mesh(geometry, mat);
        mesh.name = `ProceduralFrame_${name}`; mesh.position.x = x;
        mesh.castShadow = true; mesh.receiveShadow = true;
        root.add(mesh); proceduralPartsRef.current.push(mesh);
      });
    };
    if (proceduralFrame) {
      try { buildFrame(doorW, doorH); }
      catch (error) {
        console.error('Door frame geometry failed:', error);
        failureReasonRef.current = error instanceof Error ? error.message : 'Could not build frame geometry.';
        sceneCallbacksRef.current.error?.(props.sceneKey || '', 'Could not build frame geometry.');
        if (!renderSettingsRef.current) setUseFallback(true);
        return;
      }
    } else {
    // Bottom corners — left mirrored, right original.
    // Threshold picks the bottom material: 60mm → Oak/alu, 40mm → Oak/Pine.
    if (bottomLeftMesh) {
      place(bottomLeftMesh, -1, 1, 1, 0, { minX: 0, minY: 0 });
      place(bottomLeftMesh, 1, 1, 1, 0, { maxX: doorW, minY: 0 });
    }

    // Top corners — keep the GLB's baked Y-flip for the topleft mesh.
    if (P.topLeft) {
      place(P.topLeft, -1, -1, 1, 0, { minX: 0, maxY: doorH });
      place(P.topLeft, 1, -1, 1, 0, { maxX: doorW, maxY: doorH });
    }

    // Vertical frame bars — same baked rotation as the corners.
    // Vertical stretch goes on local Z (baked rot maps local Z to world height).
    if (P.leftBar) {
      const gapH = Math.max(0.001, doorH - corner * 2);
      const scaleZ = gapH / sizes.barHeight;
      place(P.leftBar, -1, 1, scaleZ, 0, { minX: 0, centerY: doorH / 2 });
      place(P.leftBar, 1, 1, scaleZ, 0, { maxX: doorW, centerY: doorH / 2 });
    }

    // Horizontal frame bars on top/bottom, stretched to fill between corners.
    if (P.topBar) {
      const gapW = Math.max(0.001, doorW - corner * 2);
      const scaleX = gapW / sizes.topBarWidth;
      place(P.topBar, scaleX, 1, 1, Math.PI, { centerX: doorW / 2, maxY: doorH });
    }
    if (threshold === 'threshold_alu' && P.bottomAlu) {
      // Aluminium threshold spans the full door width (under the corners too).
      const scaleX = doorW / sizes.aluBarWidth;
      place(P.bottomAlu, scaleX, 1, 1, 0, { centerX: doorW / 2, minY: 0 });
    } else if (bottomBar) {
      const gapW = Math.max(0.001, doorW - corner * 2);
      const scaleX = gapW / sizes.bottomBarWidth;
      place(bottomBar, scaleX, 1, 1, 0, { centerX: doorW / 2, minY: 0 });
    } else if (P.topBar) {
      const gapW = Math.max(0.001, doorW - corner * 2);
      const scaleX = gapW / sizes.topBarWidth;
      place(P.topBar, scaleX, 1, -1, 0, { centerX: doorW / 2, minY: 0 });
    }

    }

    // Leaf bounds — bottom clearance depends on the threshold type:
    // Derived from the profiles to retain a 4 mm running clearance.
    // (Also used for the sidelight glazing beads, which follow leaf geometry.)
    const leafBottomClearance = clearances.bottomMm / 1000;

    // Places a cloned part by its own origin and stretches it along world
    // X/Y. The stretched local axes are derived from the authored node
    // rotation, so parts exported with different orientations behave alike.
    // mirrorX = -1 mirrors about the origin (left-hand copy of a right part).
    const placeOriginStretch = (
      template: THREE.Object3D, mirrorX: number, stretchW: number, stretchH: number,
      x: number, y: number, z: number
    ) => {
      const axes = [new THREE.Vector3(1, 0, 0), new THREE.Vector3(0, 1, 0), new THREE.Vector3(0, 0, 1)]
        .map((a) => a.applyQuaternion(template.quaternion));
      const pick = (f: (a: THREE.Vector3) => number) =>
        axes.reduce((best, a, i) => (f(a) > f(axes[best]) ? i : best), 0);
      const hx = pick((a) => Math.abs(a.x));
      const vy = pick((a) => Math.abs(a.y));
      const sc = [1, 1, 1];
      sc[hx] = stretchW * mirrorX;
      sc[vy] = stretchH;
      placeAt(template, sc[0], sc[1], sc[2], x, y, z);
    };

    // Sidelights — standalone fixed panels beside the door, built from the
    // same frame parts (60mm or 40mm set depending on the family). Each
    // sidelight is a full 4-sided frame with its own corners, a glass pane
    // and optional horizontal timber dividers. Never openable.
    const legacySpec = (side: 'left' | 'right') => (
      (sidelight === `sidelight_${side}` || sidelight === 'sidelight_both')
        ? { widthMm: sidelightWidthMm, heightMm: sidelightHeightMm, glassType: 'CLEAR', layout: 'FULL_GLASS' }
        : null
    );
    const slLeftSpec = leftSidelight !== undefined ? leftSidelight : legacySpec('left');
    const slRightSpec = rightSidelight !== undefined ? rightSidelight : legacySpec('right');
    const buildSidelight = (x0: number, x1: number, spec: SidelightSpec) => {
      const slW = Math.max(0.05, (spec.widthMm || SIDELIGHT_W * 1000) / 1000);
      const slH = Math.max(0.2, (spec.heightMm || heightMm) / 1000);
      const gapH2 = Math.max(0.001, slH - corner * 2);
      const spanW = Math.max(0.001, x1 - x0 - corner * 2);
      const scaleZ = gapH2 / sizes.barHeight;
      const scaleX = spanW / sizes.topBarWidth;
      const scaleXb = spanW / sizes.bottomBarWidth;
      if (proceduralFrame) buildFrame(slW, slH, x0);
      else {
      // Four corners — same mirror/flip convention as the door frame.
      if (bottomLeftMesh) {
        place(bottomLeftMesh, -1, 1, 1, 0, { minX: x0, minY: 0 });
        place(bottomLeftMesh, 1, 1, 1, 0, { maxX: x1, minY: 0 });
      }
      if (P.topLeft) {
        place(P.topLeft, -1, -1, 1, 0, { minX: x0, maxY: slH });
        place(P.topLeft, 1, -1, 1, 0, { maxX: x1, maxY: slH });
      }
      // Vertical bars on both sides, stretched to the sidelight height.
      if (P.leftBar) {
        place(P.leftBar, -1, 1, scaleZ, 0, { minX: x0, centerY: slH / 2 });
        place(P.leftBar, 1, 1, scaleZ, 0, { maxX: x1, centerY: slH / 2 });
      }
      // Top + bottom horizontal bars between the corners.
      if (P.topBar) place(P.topBar, scaleX, 1, 1, Math.PI, { centerX: (x0 + x1) / 2, maxY: slH });
      if (bottomBar) place(bottomBar, scaleXb, 1, 1, 0, { centerX: (x0 + x1) / 2, minY: 0 });

      }

      // Glazing beads (Light_* parts) — laid out like a leaf in this opening:
      // inset LEAF_INSET from the outline, leaf bottom clearance, corners on
      // the 10 cm module, straight runs stretched between them. Authored as
      // the right-hand side; mirrored about the origin for the left.
      const L = lightPartsRef.current;
      let glassBackZ: number | null = null;
      if (L.bl && L.tl && L.left && L.top && L.bottom) {
        const m = new THREE.Box3().setFromObject(L.left).getSize(new THREE.Vector3()).y || 0.1;
        const ex0 = x0 + frameInset;
        const ex1 = x1 - frameInset;
        const by0 = leafBottomClearance;
        const by1 = slH - frameInset;
        const cxL = ex0 + m / 2;
        const cxR = ex1 - m / 2;
        const cyB = by0 + m / 2;
        const cyT = by1 - m / 2;
        const runW = Math.max(0.001, (ex1 - ex0 - 2 * m) / m);
        const runH = Math.max(0.001, (by1 - by0 - 2 * m) / m);
        const before = framePartsRef.current.length;
        [[-1, cxL], [1, cxR]].forEach(([mx, cx]) => {
          placeOriginStretch(L.bl!, mx, 1, 1, cx, cyB, LEAF_Z);
          placeOriginStretch(L.tl!, mx, 1, 1, cx, cyT, LEAF_Z);
          placeOriginStretch(L.left!, mx, 1, runH, cx, (by0 + by1) / 2, LEAF_Z);
        });
        placeOriginStretch(L.top, 1, runW, 1, (ex0 + ex1) / 2, cyT, LEAF_Z);
        placeOriginStretch(L.bottom, 1, runW, 1, (ex0 + ex1) / 2, cyB, LEAF_Z);
        // Glass rests against the beads' outer face.
        const leftBead = framePartsRef.current[before + 2];
        if (leftBead) glassBackZ = new THREE.Box3().setFromObject(leftBead).max.z;
      }

      // Glass pane inside the frame opening.
      const gw = Math.max(0.01, slW - SL_GLASS_INSET * 2);
      // Bottom edge extended SL_GLASS_DROP below the inset; top edge unchanged.
      const gh = Math.max(0.01, slH - SL_GLASS_INSET * 2 + SL_GLASS_DROP);
      const glassPane = new THREE.Mesh(
        new THREE.BoxGeometry(gw, gh, SL_GLASS_T),
        slGlassMaterial(spec.glassType)
      );
      glassPane.position.set((x0 + x1) / 2, (slH - SL_GLASS_DROP) / 2,glassBackZ !== null ? glassBackZ + SL_GLASS_T / 2 : -0.005);
      glassPane.receiveShadow = true;
      root.add(glassPane);
      sidelightPartsRef.current.push(glassPane);

      // Horizontal timber dividers — same finish as the frame colour.
      const divCount = LAYOUT_DIVIDER_COUNT[(spec.layout || 'FULL_GLASS').toUpperCase()] ?? 0;
      if (divCount > 0) {
        const matSpec = resolveColourMat(colour, customHexRef.current);
        const barMat = new THREE.MeshStandardMaterial({
          color: matSpec.color, roughness: matSpec.roughness, metalness: matSpec.metalness
        });
        colourableMatsRef.current.push(barMat);
        for (let i = 0; i < divCount; i += 1) {
          const barY = (slH - SL_GLASS_INSET * 2) * ((i + 1) / (divCount + 1)) + SL_GLASS_INSET;
          const bar = new THREE.Mesh(new THREE.BoxGeometry(gw, SL_BAR, SL_BAR * 0.6), barMat);
          bar.position.set((x0 + x1) / 2, barY, -0.002);
          bar.castShadow = true;
          root.add(bar);
          sidelightPartsRef.current.push(bar);
        }
      }
    };
    if (slLeftSpec) buildSidelight(-(Math.max(0.05, (slLeftSpec.widthMm || SIDELIGHT_W * 1000) / 1000)), 0, slLeftSpec);
    if (slRightSpec) buildSidelight(doorW, doorW + Math.max(0.05, (slRightSpec.widthMm || SIDELIGHT_W * 1000) / 1000), slRightSpec);

    // All families use a solid blank with independent side/top/bottom contours.
    // Timber families retain their authored face textures and accessories.
    // Sits inside the jamb: inset LEAF_INSET from the outline, recessed to LEAF_Z.
    const hasLeaf = P.leafBL && P.leafTL && P.leafLeft &&
      P.leafTop && P.leafBottom && P.leafMid;
    // Leaf bounds — bottom clearance depends on the threshold type:
    // Derived from the profiles to retain a 4 mm running clearance.
    const lx0 = frameInset;
    const lx1 = doorW - frameInset;
    const ly0 = leafBottomClearance;
    const ly1 = doorH - clearances.topMm / 1000;
    // The builder shortens a top-mounted leaf to the waternose's underside.
    // Keep the nominal outline here for mounting the nose and sizing the frame.
    const hasWaternose = construction.waternose.enabled;
    const leafTopY = ly1;

    // Double door: the leaf span splits into two leaves with a small meeting
    // gap. Per-leaf widths come from the layout editor; equal split is the
    // fallback when they are absent or invalid.
    const isDouble = design === 'design_double';
    const MEETING_GAP = 0.004;
    const leafRanges: [number, number][] = [];
    const leafBodyTops: number[] = [];
    if (isDouble) {
      const inner = lx1 - lx0 - MEETING_GAP;
      const wl = (leafLeftWidthMm || 0) > 0 && (leafRightWidthMm || 0) > 0
        ? inner * (leafLeftWidthMm! / (leafLeftWidthMm! + leafRightWidthMm!))
        : inner / 2;
      leafRanges.push([lx0, lx0 + wl], [lx0 + wl + MEETING_GAP, lx1]);
    } else {
      leafRanges.push([lx0, lx1]);
    }

    let leafBuildFailed = false;
    if (prefix === '60mm' || hasLeaf) {
      leafRanges.forEach(([ex0, ex1], index) => {
        let bodyTopY = leafTopY;
        const beforeLeaf = new Set(root.children);
        const exMid = (ex0 + ex1) / 2;
        const leafGapW = Math.max(0.001, ex1 - ex0 - sizes.leafW * 2);
        const leafGapH = Math.max(0.001, leafTopY - ly0 - sizes.leafH * 2);
        let parts: THREE.Object3D[] = [];
        let resolvedOpenings: { xMm: number; yMm: number; widthMm: number; heightMm: number }[] = [];
        if (leafBuilderRef.current && proceduralLeafMatRef.current) {
          const w = (ex1 - ex0) * 1000;
          const h = (leafTopY - ly0) * 1000;
          try {
            const input = { ...construction, opening, widthMm: w, heightMm: h,
              openings: leafGeometry?.openings ?? leafOpeningsForLayout(leafWindowLayout, w, h) };
            const { openings } = resolveWindowLayout(input);
            resolvedOpenings = openings;
            const geometry = leafBuilderRef.current(input);
            bodyTopY = ly0 + (geometry.boundingBox?.max.y ?? h / 1000);
            let leafSource = proceduralLeafMatRef.current;
            if (prefix === '40mm') P.leafMid?.traverse(child => {
              const mat = (child as THREE.Mesh).material as THREE.MeshStandardMaterial;
              if (mat?.name === 'Painted') leafSource = mat;
            });
            const leafMaterial = leafSource.clone();
            leafMaterial.vertexColors = geometry.hasAttribute('color');
            colourableMatsRef.current.push(leafMaterial);
            if (prefix === '40mm') cottageLeafMatsRef.current.push(leafMaterial);
            const leaf = new THREE.Mesh(geometry, leafMaterial);
            leaf.name = 'ProceduralDoorLeaf';
            leaf.position.set(ex0, ly0, LEAF_Z);
            leaf.castShadow = true;
            leaf.receiveShadow = true;
            root.add(leaf);
            proceduralPartsRef.current.push(leaf);
            parts.push(leaf);
            const cassette = construction.cassette;
            openings.forEach((opening) => {
              createWindowCassetteGeometries(opening, cassette, construction).forEach((ringGeometry) => {
                const ring = new THREE.Mesh(ringGeometry, proceduralLeafMatRef.current!);
                ring.name = 'WindowCassette';
                ring.position.set(ex0, ly0, LEAF_Z);
                root.add(ring);
                proceduralPartsRef.current.push(ring);
              });
            });
            if (glass && glass !== 'glass_none') {
              const clear = isClearLeafGlass(glass);
              openings.forEach((opening) => {
                const thickness = (thicknessMm - 2 * cassette.depthMm) / 1000;
                const pane = new THREE.Mesh(
                  new THREE.BoxGeometry(opening.widthMm / 1000, opening.heightMm / 1000, thickness),
                  slGlassMaterial(clear ? 'CLEAR' : 'FROSTED')
                );
                pane.name = 'LeafWindowGlass';
                pane.position.set(ex0 + (opening.xMm + opening.widthMm / 2) / 1000,
                  ly0 + (opening.yMm + opening.heightMm / 2) / 1000, (frontMm + backMm) / 2000);
                root.add(pane);
                proceduralPartsRef.current.push(pane);
              });
            }
          } catch (error) {
            console.error('Door leaf geometry failed:', error);
            failureReasonRef.current = error instanceof Error ? error.message : 'Could not build leaf geometry.';
            sceneCallbacksRef.current.error?.(props.sceneKey || '', error instanceof Error ? error.message : 'Could not build door geometry.');
            leafBuildFailed = true;
            if (!renderSettingsRef.current) setUseFallback(true);
            return;
          }
        } else {
          parts = [
            place(P.leafBL!, -1, 1, 1, 0, { minX: ex0, minY: ly0 }, LEAF_Z),
            place(P.leafBL!, 1, 1, 1, 0, { maxX: ex1, minY: ly0 }, LEAF_Z),
            place(P.leafTL!, -1, 1, 1, 0, { minX: ex0, maxY: leafTopY }, LEAF_Z),
            place(P.leafTL!, 1, 1, 1, 0, { maxX: ex1, maxY: leafTopY }, LEAF_Z)
          ];
          const leafScaleZ = leafGapH / sizes.leafH;
          parts.push(
            place(P.leafLeft!, -1, 1, leafScaleZ, 0, { minX: ex0, centerY: (ly0 + leafTopY) / 2 }, LEAF_Z),
            place(P.leafLeft!, 1, 1, leafScaleZ, 0, { maxX: ex1, centerY: (ly0 + leafTopY) / 2 }, LEAF_Z)
          );
          const leafScaleX = leafGapW / sizes.leafW;
          parts.push(
            place(P.leafTop!, leafScaleX, 1, 1, Math.PI, { centerX: exMid, maxY: leafTopY }, LEAF_Z),
            place(P.leafBottom!, leafScaleX, 1, 1, 0, { centerX: exMid, minY: ly0 }, LEAF_Z),
            place(P.leafMid!, leafScaleX, 1, leafScaleZ, 0, { centerX: exMid, centerY: (ly0 + leafTopY) / 2 }, LEAF_Z)
          );
        }
        // Procedural waternose shares the leaf's hinge transform and cut seat.
        if (hasWaternose && leafBuilderRef.current && proceduralLeafMatRef.current) {
          const geometry = leafBuilderRef.current.waternose({ ...construction, widthMm: (ex1-ex0)*1000, heightMm: (ly1-ly0)*1000 });
          const mesh = new THREE.Mesh(geometry, proceduralLeafMatRef.current);
          mesh.name = 'ProceduralWaternose'; mesh.position.set(ex0, ly0, LEAF_Z);
          mesh.castShadow = true; mesh.receiveShadow = true;
          root.add(mesh); proceduralPartsRef.current.push(mesh);
        }
        // CottageMiddleList — one trim strip centred on each leaf.
        if (family === 'COTTAGE' && cottagePattern === 'chevron' && construction.centreMoulding !== false && P.cottageList) {
          const clSize = new THREE.Box3().setFromObject(P.cottageList).getSize(new THREE.Vector3());
          // Stop the moulding at window cassettes instead of bridging the glass.
          const overlap = construction.cassette.overlapMm / 1000;
          const cuts = resolvedOpenings.filter(o => ex0 + o.xMm/1000 - overlap < exMid + clSize.x/2 &&
            ex0 + (o.xMm+o.widthMm)/1000 + overlap > exMid - clSize.x/2)
            .map(o => [ly0 + o.yMm/1000-overlap, ly0+(o.yMm+o.heightMm)/1000+overlap]);
          if (hasWaternose) {
            const nose = resolveWaternose(construction.waternose, (ly1-ly0)*1000);
            cuts.push([ly0+(nose.yMm+Math.min(...nose.profileMm.map(p=>p[1])))/1000,
              ly0+(nose.yMm+Math.max(...nose.profileMm.map(p=>p[1])))/1000]);
          }
          cuts.sort((a,b)=>a[0]-b[0]);
          const segments: number[][] = []; let cursor = ly0;
          cuts.forEach(([bottom,top]) => { if (bottom > cursor) segments.push([cursor,bottom]); cursor = Math.max(cursor,top); });
          if (cursor < bodyTopY) segments.push([cursor,bodyTopY]);
          segments.filter(([bottom]) => bottom < bodyTopY).forEach(([bottom,uncutTop]) => {
          const top = Math.min(uncutTop, bodyTopY);
          const cl = place(
            P.cottageList,
            1, 1,
            Math.max(0.001, (top - bottom) / Math.max(0.001, clSize.y)),
            0,
            {
              centerX: exMid + COTTAGE_LIST_OFFSET_X,
              centerY: (bottom + top) / 2 + COTTAGE_LIST_OFFSET_Y
            },
            0
          );
          if (cl) {
            const clBox = new THREE.Box3().setFromObject(cl);
            cl.position.z += backMm / 1000 - clBox.max.z;
          }
          });
        }
        // Cottage/Meranti/18_DEG leaf textures — projected per leaf so the
        // pattern centres on each leaf instead of stretching across both.
        if (family === 'COTTAGE' || family === 'MERANTI' || family === '18_DEG') {
          const center = new THREE.Vector3(exMid, (ly0 + bodyTopY) / 2, LEAF_Z);
          const surface = timberLeafSurface(family, cottagePattern);
          if (surface) {
            parts.forEach((p) => {
              if (!p) return;
              projectWorldUVs(p, center, surface.uvScale, 'uv');
              if (surface.reliefChannel === 'uv1' && surface.reliefScale) {
                projectWorldUVs(p, center, surface.reliefScale, 'uv1');
              }
            });
          } else {
            parts.forEach((p) => p && projectWorldUVs(p, center, MDF_TEX_SCALE));
          }
        }
        // Local hinge motion is outward; the root reflection handles inward.
        const axis = doorOpeningAxis({ leftMm: ex0 * 1000, rightMm: ex1 * 1000, handing, opening: 'outward', index, double: isDouble });
        let members = root.children.filter((child) => !beforeLeaf.has(child));
        const mirrored = orientLeafDesignForExterior(members, ex0 * 1000, ex1 * 1000, index, isDouble);
        if (mirrored) {
          root.add(mirrored);
          members = [mirrored];
        }
        const group = new THREE.Group(); group.name = `DoorLeafPivot_${index}`;
        group.position.set(axis.x, 0, axis.z); root.add(group); root.updateMatrixWorld(true);
        members.forEach((child) => group.attach(child));
        leafPivotsRef.current.push({ group, sign: axis.sign, active: axis.active });
        leafBodyTops[index] = bodyTopY;
      });

      // Hardware — handing only moves the hardware; frame, sidelights and
      // leaf pattern stay put. Left handing: hinges on the left jamb, lock on
      // the right edge (double door: left leaf active). Right handing is the
      // mirror: hinges right, lock left (double door: right leaf active).
      // `lockDir` is +1 when the lock sits on the leaf's right edge, -1 left;
      // Handles are authored for +1. The Lock mesh is the opposite: its bolts
      // project toward -X, with the 3 mm faceplate at local X = 5.5…8.5 mm.
      const rightHanded = handing === 'right';
      leafRanges.forEach(([ex0, ex1], idx) => {
        const beforeHardware = new Set(root.children);
        const hingeEdge = isDouble ? (idx === 0 ? ex0 : ex1) : (rightHanded ? lx1 : lx0);
        // Outward direction toward the jamb: left edge → -1, right edge → +1.
        const hingeOut = isDouble ? (idx === 0 ? -1 : 1) : (rightHanded ? 1 : -1);
        const isActive = !isDouble || idx === (rightHanded ? 1 : 0);
        const lockDir = isDouble ? (idx === 0 ? 1 : -1) : (rightHanded ? -1 : 1);
        const lockEdge = lockDir > 0 ? ex1 : ex0;
        // Public presets and double leaves also need the height-band hinge rule,
        // even when no Studio hardware snapshot accompanies the configuration.
        const resolvedHardware = resolveDoorHardware({ family: family || '60_OUT', threshold: selectedThreshold,
          hardware: { ...leafGeometry?.hardware, hinges: leafGeometry?.hardware?.hinges ?? (hinges === 2 ? 2 : 3) },
          nominalHeightMm: (ly1-ly0)*1000, bodyHeightMm: ((leafBodyTops[idx] ?? ly1)-ly0)*1000,
          assemblyHeightMm: doorH*1000, leafBottomMm: ly0*1000 });
        const hardwareLayout = leafGeometry?.hardware ? resolvedHardware : null;
        const lockY = hardwareLayout ? hardwareLayout.lock.floorCenterMm/1000 : LOCK_Y;
        const handleX = lockEdge - lockDir * (hardwareLayout?.lock.backsetMm ? hardwareLayout.lock.backsetMm/1000 : HANDLE_X_INSET);

        if (isActive && lockRef.current) {
          const lockPlacement = doorLockPlacement(lockEdge, lockDir, {
            backMm, edgeOffsetMm: resolvedHardware.lock.edgeOffsetMm ?? 12.8,
            faceplateWidthMm: resolvedHardware.lock.faceplateWidthMm ?? 22,
            sideProfileMm: construction.sideProfileMm
          });
          placeAt(lockRef.current, lockPlacement.scaleX, 1, 1, lockPlacement.x, lockY, LEAF_Z + lockPlacement.z);
        }
        if (isActive && lockCoverRef.current) {
          const coverY = hardwareLayout ? lockY - 0.0555 : LOCK_COVER_Y;
          place(lockCoverRef.current, lockDir, 1, 1, 0, { centerX: handleX, centerY: coverY }, frontMm / 1000);
          place(lockCoverRef.current, lockDir, -1, -1, 0, { centerX: handleX, centerY: coverY }, backMm / 1000 + 0.0005);
        }
        if (isActive && handleRef.current) {
          const handleY = hardwareLayout ? lockY + 0.0555 : HANDLE_Y;
          placeAt(handleRef.current, lockDir, -1, 1, handleX, handleY, frontMm / 1000);
          placeAt(handleRef.current, lockDir, 1, -1, handleX, handleY, backMm / 1000 + 0.0005);
        }
        const pivot = leafPivotsRef.current[idx];
        if (pivot) root.children.filter((child) => !beforeHardware.has(child) && child !== pivot.group)
          .forEach((child) => pivot.group.attach(child));
        const hingeZ = pivot?.group.position.z ?? HINGE_Z;
        // Hinges on the leaf's outer (jamb) edge — bottom, middle, top
        // (middle skipped for 2-hinge), mirrored for the right leaf.
        if (hingeRef.current) {
          const hx = hingeEdge + hingeOut * HINGE_JAMB_OFFSET;
          const sx = -hingeOut;
          const hingeHeights = resolvedHardware.hinges.floorCentersMm.map((y: number) => y/1000);
          hingeHeights.forEach((centerY) => {
            // The source mesh is one hinge half. Its lower barrel/plate mounts
            // to the jamb; the flipped upper half mounts to the moving leaf.
            const lower = place(hingeRef.current!, sx, -1, 1, 0, { centerX: hx, centerY }, hingeZ);
            lower.name = 'HingeFrameHalf';
            const upper = place(hingeRef.current!, -sx, -1, -1, 0, { centerX: hx, centerY }, hingeZ);
            upper.name = 'HingeLeafHalf';
            pivot?.group.attach(upper);
          });
        }
      });
    } else {
      // Simple centre leaf placeholder until leaf parts exist in the GLB.
      const leafW = Math.max(0.001, doorW - corner * 2);
      const leafH = Math.max(0.001, doorH - corner * 2);
      const matSpec = resolveColourMat(colour, customHexRef.current);
      const leafMat = new THREE.MeshStandardMaterial({
        color: matSpec.color,
        roughness: matSpec.roughness,
        metalness: matSpec.metalness
      });
      colourableMatsRef.current.push(leafMat);
      const leaf = new THREE.Mesh(new THREE.BoxGeometry(leafW, leafH, 0.025), leafMat);
      leaf.position.set(doorW / 2, doorH / 2, 0.02);
      leaf.castShadow = true;
      leaf.receiveShadow = true;
      root.add(leaf);
      leafPartRef.current = leaf;
    }
    // Keep the orbit pivot on the centre of the whole set (door + sidelights):
    // shift camera and target by the same delta so the user's angle and zoom
    // are preserved while rotation stays centred after layout/size changes.
    const controls = controlsRef.current;
    const setBox = new THREE.Box3().setFromObject(root);
    if (controls && !setBox.isEmpty()) {
      const centre = setBox.getCenter(new THREE.Vector3());
      const delta = centre.sub(controls.target);
      controls.object.position.add(delta);
      controls.target.add(delta);
      controls.update();
      lastSetBoxRef.current = setBox.clone();
      fitToBoxRef.current?.(setBox);
    }
    if (leafBuildFailed) {
      sceneCallbacksRef.current.error?.(props.sceneKey || '', 'Could not build leaf geometry.');
      return;
    }
    sceneBuiltKeyRef.current = props.sceneKey || '';
    lightingDirtyRef.current = true;
    sceneCallbacksRef.current.ready?.(props.sceneKey || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widthMm, heightMm, status, colour, design, glass, handing, opening, threshold, hinges, family, sidelight, sidelightWidthMm, sidelightHeightMm, leftSidelight, rightSidelight, waternose, leafLeftWidthMm, leafRightWidthMm, leafWindowLayout, leafGeometry, cottagePattern, props.sceneKey, DEV_BUILD]);

  if (useFallback) {
    return <PreviewFailure error={failureReasonRef.current} onRetry={() => {
      setStatus('loading');
      setUseFallback(false);
    }} />;
  }

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', minHeight: props.renderSettings ? 0 : '12rem' }}>
      {status === 'loading' && <Spinner />}
      <div ref={containerRef} style={{ position: 'absolute', inset: 0 }} />
      {status === 'ready' && !props.renderSettings && <div style={{ position: 'absolute', bottom: 12, left: 12, right: 12,
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, padding: '8px 10px',
        borderRadius: 8, background: '#101721df', color: '#eef2f7', fontSize: 11 }}>
        <button type="button" onClick={() => { const value = openAngle > 0 ? 0 : 80; setOpenAngle(value); props.onPreviewAngleChange?.(value); }}
          style={{ background: 'transparent', color: '#e9bd52', border: '1px solid #6b572a', borderRadius: 4, padding: '5px 8px', cursor: 'pointer' }}>
          {openAngle > 0 ? 'Close door' : 'Open door'}</button>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
          <span>Opening {openAngle}°</span><input aria-label="Door opening angle" type="range" min="0" max="110" value={openAngle}
            style={{ minWidth: 60, width: '100%', accentColor: '#dfa926' }} onChange={(e) => { const value = Number(e.target.value); setOpenAngle(value); props.onPreviewAngleChange?.(value); }} /></label>
        {design === 'design_double' && <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          Secondary leaf {passiveAngle}°<input aria-label="Secondary leaf opening angle" type="range" min="0" max="110" value={passiveAngle}
            style={{ width: 80, accentColor: '#dfa926' }} onChange={(e) => { const value = Number(e.target.value); setPassiveAngle(value); props.onPreviewPassiveAngleChange?.(value); }} /></label>}
      </div>}
    </div>
  );
});

DoorPreviewGLB.displayName = 'DoorPreviewGLB';

export default DoorPreviewGLB;
